/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React from 'react';
import { Form } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { UploadFile } from 'antd/lib/upload/interface';
import { isNil } from 'rambda';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputNumber } from 'components/Input/InputNumber';
import { Input } from '../../../../components/Input/Input';
import { FormItem, FormItemProps } from '../../../../components/Form/FormItem';
import { ImageUpload } from '../../../../components/ImageUpload/ImageUpload';
import { useInventorizeStepsStore } from '../../components/store';
import { FORM_LAYOUT_CONFIG } from '../../../../consts/common';
import { useStore } from '../../useStore';
import { useScrollTopOnMount } from '../../../../hooks/useScrollTopOnMount';
import { DetailStepForm } from './slice';
import { AdditionalFields } from './additional_fields/AdditionalFields';
import { useMeterContext } from '../../../../contexts/Meter';
import { getSerialNumberTooltip } from './utils/getSerialNumberTooltip';
import { DetailStepContent as messages } from './messages';
import { MeterModelDivider } from '../../../../components/Divider/MeterModelDivider';
import { AccessibilitySelect } from '../../../../components/Select/Accessibility';

export const DetailStepContent: React.VFC = () => {
  const { inventoryMeter } = useMeterContext();
  const intl = useIntl();

  const { setNextStep } = useInventorizeStepsStore();

  const {
    accessibility,
    newMeter,
    commodity,
    photoDetailList,
    lorawanRssi,
    nbiotRssi,
    serialNumber,
    detail,
    internalNote,
    setDetailStepValues,
    setPhotoDetailList,
  } = useStore();

  useScrollTopOnMount();

  const [form] = Form.useForm();

  const checkSignal = (_: unknown, value: number) => {
    // Empty value is not allowed in create mode
    if (!value && !inventoryMeter?.id) {
      return Promise.reject(new Error(''));
    }

    // Validate value or allow empty for edit mode
    if (value > 0 || !value) {
      return Promise.resolve();
    }

    return Promise.reject(new Error(''));
  };

  const handleValuesChange = (changedValue: Partial<DetailStepForm>, allVals: DetailStepForm) => {
    // Reset selected flowrate value when unit is changed
    if (
      changedValue?.detail &&
      'flowrate' in changedValue.detail &&
      typeof changedValue.detail?.flowrate?.unit === 'string'
    ) {
      const emptyFlowrateValue = {
        detail: {
          ...detail,
          flowrate: { value: null, unit: changedValue.detail.flowrate.unit },
        },
      };

      setDetailStepValues(emptyFlowrateValue);
      return form.setFieldsValue(emptyFlowrateValue);
    }

    if ('twinMeter' in changedValue && changedValue?.twinMeter === 0) {
      const emptySecondSerialNumberValue = {
        detail: { ...detail, secondSerialNumber: null },
        ...changedValue,
      };

      setDetailStepValues(emptySecondSerialNumberValue);
      return form.setFieldsValue(emptySecondSerialNumberValue);
    }

    // Reset selected circuit_breaker_size value when phases are changed
    if (changedValue?.detail && 'phases' in changedValue.detail) {
      const emptyCircuitBreaker = {
        detail: { ...detail, circuitBreakerSize: undefined, phases: changedValue.detail.phases },
      } as DetailStepForm;

      setDetailStepValues(emptyCircuitBreaker);
      return form.setFieldsValue(emptyCircuitBreaker);
    }

    return setDetailStepValues(allVals);
  };

  const handleFormItemReset = (namePath: FormItemProps['name']) => {
    const name = Array.isArray(namePath) ? namePath.at(-1) : namePath;

    if (isNil(namePath) || isNil(name)) return;

    form.setFieldValue(namePath, null);
    setDetailStepValues({
      [name]: null,
    });
  };

  const handleSubmit = () => {
    setNextStep({ commodity, newMeter });
  };

  const handleUploadImg = (fileList: UploadFile[]) => {
    setPhotoDetailList(fileList);
    form.setFieldsValue({ meterPhotoDetailList: fileList });
  };

  return (
    <Form
      form={form}
      id="detailStepForm"
      layout="horizontal"
      name="detailStepForm"
      onFinish={handleSubmit}
      onValuesChange={handleValuesChange}
      {...FORM_LAYOUT_CONFIG}
    >
      <FormItem
        help={<FormattedMessage {...messages.detailPhotosHelp} />}
        initialValue={photoDetailList}
        label={<FormattedMessage {...messages.detailPhotosLabel} />}
        name="meterPhotoDetailList"
        required={!inventoryMeter?.id}
        rules={[
          {
            required: !inventoryMeter?.id,
            message: intl.formatMessage(messages.detailPhotosRequired),
          },
        ]}
        tooltip={<FormattedMessage {...messages.detailPhotosTooltip} />}
      >
        <ImageUpload multiple fileList={photoDetailList} name="meterPhotoDetailList" onImageUpload={handleUploadImg} />
      </FormItem>

      {!newMeter && (
        <FormItem
          initialValue={serialNumber}
          isSemiRequired={!inventoryMeter?.id}
          label={<FormattedMessage {...messages.serialNumber} />}
          name="serialNumber"
          tooltip={getSerialNumberTooltip(commodity, intl)}
          onResetField={handleFormItemReset}
        >
          <Input placeholder="17w700028" />
        </FormItem>
      )}

      <FormItem
        initialValue={lorawanRssi}
        isSemiRequired={!inventoryMeter?.id}
        label={<FormattedMessage {...messages.lorawanRssi} />}
        name="lorawanRssi"
        rules={[
          {
            validator: checkSignal,
            validateTrigger: 'onSubmit',
            required: !inventoryMeter?.id,
            message: intl.formatMessage(messages.lorawanRssiRequired),
          },
        ]}
        tooltip={<FormattedMessage {...messages.lorawanRssiTooltip} />}
        onResetField={handleFormItemReset}
      >
        <InputNumber addonAfter="dBm" className="w-100" placeholder="66" precision={0} prefix="-" />
      </FormItem>
      {/* NB-IoT RSSI field */}
      <FormItem
        initialValue={nbiotRssi}
        isSemiRequired={!inventoryMeter?.id}
        label={<FormattedMessage {...messages.nbiotRssi} />}
        name="nbiotRssi"
        rules={[
          {
            validator: checkSignal,
            validateTrigger: 'onSubmit',
            required: !inventoryMeter?.id,
            message: intl.formatMessage(messages.nbiotRssiRequired),
          },
        ]}
        tooltip={<FormattedMessage {...messages.lorawanRssiTooltip} />}
        onResetField={handleFormItemReset}
      >
        <InputNumber addonAfter="dBm" className="w-100" placeholder="66" precision={0} prefix="-" type="number" />
      </FormItem>

      <FormItem
        required
        initialValue={accessibility ?? []}
        label={<FormattedMessage {...messages.accessibility} />}
        name="accessibility"
        rules={[
          {
            required: true,
            message: intl.formatMessage(messages.accessibilityRequired),
          },
        ]}
      >
        <AccessibilitySelect />
      </FormItem>

      <MeterModelDivider />

      <AdditionalFields commodity={commodity} />

      <FormItem initialValue={internalNote} label={<FormattedMessage {...messages.note} />} name="internalNote">
        <TextArea autoSize={{ minRows: 3, maxRows: 5 }} placeholder={intl.formatMessage(messages.notePlaceholder)} />
      </FormItem>
    </Form>
  );
};
